import { useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';
import { LOGIN_LABELS } from '../../../utils/labels';

interface UseLoginReturn {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  error: string;
  loading: boolean;
  magicSent: boolean;
  forgotView: boolean;
  resetSent: boolean;
  signIn: (e: FormEvent) => Promise<void>;
  sendMagicLink: () => Promise<void>;
  sendResetEmail: () => Promise<void>;
  showForgot: () => void;
  hideForgot: () => void;
}

export function useLogin(): UseLoginReturn {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [forgotView, setForgotView] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) setError(LOGIN_LABELS.invalidCredentials);
    } catch {
      setError(LOGIN_LABELS.invalidCredentials);
    } finally {
      setLoading(false);
    }
  };

  const sendMagicLink = async () => {
    if (!email) {
      setError(LOGIN_LABELS.emailRequired);
      return;
    }
    setLoading(true);
    try {
      await supabase.auth.signInWithOtp({ email });
      setMagicSent(true);
    } catch {
      setError(LOGIN_LABELS.invalidCredentials);
    } finally {
      setLoading(false);
    }
  };

  const sendResetEmail = async () => {
    if (!email) {
      setError(LOGIN_LABELS.emailRequired);
      return;
    }
    setLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      setResetSent(true);
    } catch {
      setError(LOGIN_LABELS.resetError);
    } finally {
      setLoading(false);
    }
  };

  const showForgot = () => {
    setError('');
    setForgotView(true);
  };

  const hideForgot = () => {
    setError('');
    setForgotView(false);
    setResetSent(false);
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    error,
    loading,
    magicSent,
    forgotView,
    resetSent,
    signIn,
    sendMagicLink,
    sendResetEmail,
    showForgot,
    hideForgot,
  };
}
